import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Live notification counters: unseen likes, unseen matches, unread messages.
// Polled from GET /notifications/summary every 12s while the tab is VISIBLE
// (hidden tabs skip the fetch to save battery and server load), plus instant
// refreshes on focus/visibilitychange and right after a swipe (refreshNow).
// Pages that display a kind call markSeen(kind) which zeroes it optimistically
// and persists via POST /notifications/seen. Messages drain by reading chats.
const ZERO = { likes: 0, matches: 0, messages: 0, total: 0 };

const NotificationsContext = createContext({
  ...ZERO,
  refresh: () => {},
  refreshNow: () => {},
  markSeen: () => {},
});

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [counts, setCounts] = useState(ZERO);
  const uid = user && !user.demo ? user.id || user._id : null;

  const refresh = useCallback(() => {
    if (!uid) return;
    api
      .get("/notifications/summary")
      .then((res) => {
        const d = res.data || {};
        setCounts({
          likes: d.likes || 0,
          matches: d.matches || 0,
          messages: d.messages || 0,
          total: d.total || 0,
        });
      })
      .catch(() => {});
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setCounts(ZERO);
      return undefined;
    }
    refresh();
    // 12s poll, but ONLY while the tab is visible: a badge appearing within
    // seconds is the point, yet a backgrounded tab hammering the API drains
    // phone batteries for counters nobody is looking at.
    const id = setInterval(() => {
      if (!document.hidden) refresh();
    }, 12000);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    // WHY the extra focus listener: two windows SIDE BY SIDE (exactly how a
    // two-account test is run) never fire visibilitychange — both stay
    // visible — so a fresh match or message would sit invisible for up to
    // 45s and read as "no badge". Clicking into the window fires focus and
    // refreshes the counters immediately.
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
    };
  }, [uid, refresh]);

  const markSeen = useCallback(
    (kind) => {
      setCounts((c) => ({
        ...c,
        [kind]: 0,
        total: Math.max(0, c.total - (c[kind] || 0)),
      }));
      if (uid) api.post("/notifications/seen", { kind }).catch(() => {});
    },
    [uid]
  );

  // refreshNow: same fetch as refresh, exported under an intent-revealing
  // name so swipe decks can pull fresh counters the moment a swipe lands
  // (a swipe can create a match, and waiting a poll cycle feels broken).
  return (
    <NotificationsContext.Provider value={{ ...counts, refresh, refreshNow: refresh, markSeen }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationsContext);

// The one badge style used everywhere a counter renders (brand: emerald).
export function CountBadge({ count, className = "" }) {
  if (!count) return null;
  return (
    <span
      className={`flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-primary px-1.5 text-[10px] font-semibold leading-none text-white ${className}`}
    >
      {count > 9 ? "9+" : count}
    </span>
  );
}
